// components/MarginChatBubble.tsx

import {
  MarginDecision,
  MarginDecisionEnum,
  User,
  UserOnEntity,
} from "@/graphql/__generated__/graphql";
import {
  convertBpToDecimal,
  formatDateLocale,
  formatNumberLocale,
} from "@/utils/helpers";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { Card } from "@/components/ui/card";
import { ChatBubbleBase } from "../../Components";
import { JSX } from "react";

interface Props {
  marginDecision: MarginDecision;
  autoApproved: boolean;
  tenderUsers: UserOnEntity[];
};

/**
 * Component representing a chat bubble for margin decisions.
 *
 * @param {Props} props - The properties object.
 * @param {MarginDecision} props.marginDecision - The margin decision object containing the decision details.
 * @returns {JSX.Element} The rendered JSX element for the margin chat bubble.
 *
 * This component handles the rendering of a chat bubble based on the margin decision.
 * It uses various helper functions to determine the appropriate title text prefix,
 * CSS class for the border color, and other details based on the margin decision.
 *
 * The component also handles a special case where the decision is a request for margin approval.
 * In this case, it renders a card with detailed information about the margin request.
 * If the margin exceeds the maximum allowed margin, an auto-approve message is displayed.
 *
 * For other decision types, the component uses the `ChatBubbleBase` component to render the chat bubble.
 */
const MarginChatBubble = ({ marginDecision, tenderUsers, autoApproved }: Props): JSX.Element => {

  // Hooks
  const { t } = useTranslation(["components", "common"]);
  const intl = useIntl();

  /**
   * Returns the appropriate title text prefix based on the given margin decision.
   *
   * @param {MarginDecision} decision - The margin decision object containing the decision type.
   * @returns {string} The translated title text prefix for the given decision.
   */
  const getTitleTextPrefix = (decision: MarginDecision): string => {
    const currentDecision = decision.decision;
    if (currentDecision === MarginDecisionEnum.Approve) {
      return t("chatbubble.approvequote");
    } else if (currentDecision === MarginDecisionEnum.Reject) {
      return t("chatbubble.rejectquote");
    } else if (currentDecision === MarginDecisionEnum.OverruleApprove) {
      return t("chatbubble.overruleapprove");
    } else if (currentDecision === MarginDecisionEnum.OverruleReject) {
      return t("chatbubble.overrulereject");
    }
    return "";
  };

  /**
   * Returns the CSS class for the border color based on the margin decision.
   *
   * @param {MarginDecision} decision - The margin decision object containing the decision and margin.
   * @returns {string} - The CSS class for the border color.
   *
   * The function checks the decision and margin properties of the MarginDecision object:
   * - If the decision is `Approve` and the margin is `null`, it returns the CSS class for a green border.
   * - If the decision is `Reject`, it returns the CSS class for a red border.
   * - Otherwise, it returns an empty string.
   */
  const getDecisionColour = (decision: MarginDecision): string => {
    if (
      (decision.decision === MarginDecisionEnum.Approve || decision.decision === MarginDecisionEnum.OverruleApprove) &&
      decision.margin === null
    ) {
      return "border-green-500";
    } else if (decision.decision === MarginDecisionEnum.Reject || decision.decision === MarginDecisionEnum.OverruleReject) {
      return "border-red-500";
    }
    return "";
  };

  /**
   * Returns the background colour based on the margin decision.
   *
   * @param {MarginDecision} decision - The margin decision object containing the decision type.
   * @returns {string} - The background colour class based on the decision type.
   *
   * The function checks the decision property of the MarginDecision object:
   * - If the decision is `OverruleApprove`, it returns the CSS class for a green background.
   * - If the decision is `OverruleReject`, it returns the CSS class for a red background.
   * - Otherwise, it returns an empty string.
   */
  const getBackgroundColour = (decision: MarginDecision): string => {
    if (decision.decision === MarginDecisionEnum.OverruleApprove) {
      return "bg-green-100";
    } else if (decision.decision === MarginDecisionEnum.OverruleReject) {
      return "bg-red-100";
    }

    return "";
  };

  // Getter functions
  const getComment = (decision: MarginDecision): string => decision.comment ?? "";
  const getDate = (decision: MarginDecision): string => decision.createdAt;
  const getUser = (decision: MarginDecision): User => decision.userOnDecision as User;
  const getUserName = (decision: MarginDecision): string => {
    const firstname = decision.userOnDecision?.firstname ?? "";
    const lastname = decision.userOnDecision?.lastname ?? "";
    return `${firstname} ${lastname}`.trim();
  };
  const getOrganizationName = (decision: MarginDecision): string =>
    decision.userOnDecision?.organization?.name ?? "";

  if (marginDecision.decision === MarginDecisionEnum.Request) {
    // *** Handle the special case
    return (
      <div>
        <Card className="text-base text-gray-400 p-4">
          <p>
            {t("chatbubble.requestmarginapproval", { user: `${marginDecision?.userOnDecision?.firstname} ${marginDecision?.userOnDecision?.lastname}` })}
          </p>
          <div className="md:flex justify-between gap-2 text-gray-500 text-sm">
            <p>{t("chatbubble.labels.margin")} <span className="font-semibold">{convertBpToDecimal(marginDecision.margin as number)}%</span></p>
            <p>{t("chatbubble.labels.revenue")} <span className="font-semibold">{formatNumberLocale(intl, marginDecision.revenue ?? 0, "currency", true)}</span></p>
            <p>{t("chatbubble.labels.totalcost")} <span className="font-semibold">{formatNumberLocale(intl, marginDecision.totalCost ?? 0, "currency", true)}</span></p>
            <p>{t("chatbubble.labels.profit")} <span className="font-semibold">{formatNumberLocale(intl, (marginDecision.revenue ?? 0) - (marginDecision.totalCost ?? 0), "currency", true)}</span></p>
            <p>{t("chatbubble.labels.hours")} <span className="font-semibold">{formatNumberLocale(intl, ((marginDecision.minutes ?? 0) as number) / 60, "decimal")}</span></p>
          </div>
          <div className="flex justify-end mt-2">
            <p className="text-xs text-gray-400 font-light whitespace-nowrap">
              {formatDateLocale(intl, marginDecision.createdAt, {
                year: "numeric",
                month: "numeric",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
              })}
            </p>
          </div>
        </Card>
        {autoApproved && (
          <div className="mt-4 flex justify-center">
            <p className="text-gray-400 text-sm">
              {t("chatbubble.autoapprovemessage")}
            </p>
          </div>
        )}
      </div>
    );
  } else {
    // Use ChatBubbleBase for the common case
    return (
      <ChatBubbleBase
        decision={marginDecision}
        getDecisionColour={getDecisionColour}
        getBackgroundColour={getBackgroundColour}
        getTitleTextPrefix={(decision) =>
          getTitleTextPrefix(decision as MarginDecision)
        }
        getComment={getComment}
        getDate={getDate}
        getUser={getUser}
        getUserName={getUserName}
        getOrganizationName={getOrganizationName}
        tenderUsers={tenderUsers}
      />
    );
  }
};

export default MarginChatBubble;
